import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, VpnKey as VpnKeyIcon } from '@mui/icons-material';
import { useUsers, useUserMutations, useResetPassword } from '../services/api';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

const UsersPage = () => {
  const { data: users = [], isLoading } = useUsers();
  const [open, setOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    phone: '',
    isAdmin: false,
    isApproved: false,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();
  const { createUser, updateUser } = useUserMutations();
  const resetPasswordMutation = useResetPassword();
  const [resetPasswordDialog, setResetPasswordDialog] = useState({
    open: false,
    userId: null,
    newPassword: '',
    confirmPassword: ''
  });

  const handleCreateUser = async (userData) => {
    try {
      await createUser.mutateAsync({
        email: userData.email,
        password: userData.password,
        fullName: userData.fullName,
        phone: userData.phone,
      });
      setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to create user', 
        severity: 'error' 
      });
    }
  };

  const handleUpdateUser = async (id, userData) => {
    try {
      await updateUser.mutateAsync({ id, ...userData });
      setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
    } catch (error) {
      setSnackbar({ 
        open: true, 
        message: error.response?.data?.message || 'Failed to update user', 
        severity: 'error' 
      });
    }
  };

  const handleOpen = (user = null) => {
    setEditingUser(user);
    if (user) {
      setFormData({
        email: user.email,
        password: '',
        fullName: user.fullName,
        phone: user.phone,
        isAdmin: user.isAdmin,
        isApproved: user.isApproved,
      });
    } else {
      setFormData({
        email: '',
        password: '',
        fullName: '',
        phone: '',
        isAdmin: false,
        isApproved: false,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditingUser(null);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Don't include password in update if it's not changed
    const dataToSubmit = { ...formData };
    if (editingUser && !dataToSubmit.password) {
      delete dataToSubmit.password;
    }

    if (editingUser) {
      await handleUpdateUser(editingUser.id, dataToSubmit);
    } else {
      await handleCreateUser(dataToSubmit);
    }
    handleClose();
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenResetPassword = (userId) => {
    setResetPasswordDialog({
      open: true,
      userId,
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleCloseResetPassword = () => {
    setResetPasswordDialog({
      open: false,
      userId: null,
      newPassword: '',
      confirmPassword: ''
    });
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const { userId, newPassword, confirmPassword } = resetPasswordDialog;
    
    if (newPassword !== confirmPassword) {
      setSnackbar({
        open: true,
        message: 'Passwords do not match',
        severity: 'error'
      });
      return;
    }

    try {
      await resetPasswordMutation.mutateAsync({
        token: userId,
        newPassword,
        confirmNewPassword: confirmPassword
      });
      
      setSnackbar({
        open: true,
        message: 'Password reset successfully',
        severity: 'success'
      });
      
      handleCloseResetPassword();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to reset password',
        severity: 'error'
      });
    }
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">Users</Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpen()}
          >
            Add User
          </Button>
        </Box>

        <Card>
          <CardContent>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>Admin</TableCell>
                    <TableCell>Approved</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>{user.fullName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.phone || 'N/A'}</TableCell>
                      <TableCell>{user.isAdmin ? 'Yes' : 'No'}</TableCell>
                      <TableCell>{user.isApproved ? 'Yes' : 'No'}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleOpen(user)} title="Edit User">
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleOpenResetPassword(user.id)}
                          title="Reset Password"
                          color="secondary"
                        >
                          <VpnKeyIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  margin="normal"
                  disabled={!!editingUser}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              {!editingUser && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!editingUser}
                    margin="normal"
                  />
                </Grid>
              )}
              {editingUser && (
                <>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isAdmin}
                          onChange={(e) =>
                            setFormData(prev => ({
                              ...prev,
                              isAdmin: e.target.checked
                            }))
                          }
                          name="isAdmin"
                          color="primary"
                        />
                      }
                      label="Is Admin"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isApproved}
                          onChange={(e) =>
                            setFormData(prev => ({
                              ...prev,
                              isApproved: e.target.checked
                            }))
                          }
                          name="isApproved"
                          color="primary"
                        />
                      }
                      label="Is Approved"
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary">
              {editingUser ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialog.open} onClose={handleCloseResetPassword} maxWidth="sm" fullWidth>
        <form onSubmit={handleResetPassword}>
          <DialogTitle>Reset User Password</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Password"
                  type="password"
                  value={resetPasswordDialog.newPassword}
                  onChange={(e) => setResetPasswordDialog(prev => ({
                    ...prev,
                    newPassword: e.target.value
                  }))}
                  required
                  margin="normal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm New Password"
                  type="password"
                  value={resetPasswordDialog.confirmPassword}
                  onChange={(e) => setResetPasswordDialog(prev => ({
                    ...prev,
                    confirmPassword: e.target.value
                  }))}
                  required
                  margin="normal"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseResetPassword}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={resetPasswordMutation.isLoading}
            >
              {resetPasswordMutation.isLoading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UsersPage;
